import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MagnifyingGlass, DownloadSimple, X, ArrowLeft, Receipt, PencilSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "../components/Brutal";
import EditMovementDialog from "../components/EditMovementDialog";
import { useAuth } from "../AuthContext";

/**
 * Sales History / Pricing Audit
 * =============================
 * Answers: "What did Chantel pay for KLOW last time?" and every variation of it.
 *
 * Filters: customer (partial-match), product (partial-match), date range,
 * include-cancelled toggle. All filters debounce-fire the query and hit
 * GET /api/sales-history. Pagination is offset-based via a Load more button.
 *
 * Layout:
 *   - Sticky filter bar on top so it survives long scrolls (floor use).
 *   - Table on ≥md, per-row card stack on <md so distributors can read on phones.
 *   - CSV export button is always visible in the header.
 */
export default function SalesHistoryPage() {
  const { t, i18n } = useTranslation();
  const { user, isSuperOwner } = useAuth();
  // v403.4 — log editing is owner/admin ONLY.
  // v406.2 — allowlisted super-owners (Harvey/Sukhinder) get god-mode parity.
  const canEditLogs = !!user && user !== false && (["owner", "admin"].includes(user.role) || isSuperOwner);
  const [editTarget, setEditTarget] = useState(null);
  const [customer, setCustomer] = useState("");
  const [product, setProduct] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ total_units: 0, total_amount: 0 });
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const debounceRef = useRef(null);

  // Read ?customer= from the URL on first mount so the "Total revenue"
  // card on the dashboard can deep-link with a pre-filled filter.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("customer")) setCustomer(p.get("customer"));
    if (p.get("product")) setProduct(p.get("product"));
  }, []);

  const params = useMemo(() => {
    const p = {};
    if (customer.trim()) p.customer = customer.trim();
    if (product.trim()) p.product = product.trim();
    if (dateFrom) p.from = dateFrom;
    if (dateTo) p.to = dateTo;
    if (includeCancelled) p.include_cancelled = true;
    return p;
  }, [customer, product, dateFrom, dateTo, includeCancelled]);

  const load = async (nextOffset = 0, append = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/sales-history", {
        params: { ...params, offset: nextOffset, limit: 50 },
      });
      setItems((prev) => (append ? [...prev, ...data.items] : data.items));
      setTotalCount(data.total);
      setTotals(data.totals);
      setHasMore(data.has_more);
      setOffset(nextOffset);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Debounce so a typing user doesn't fire a request per keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(0, false), 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, product, dateFrom, dateTo, includeCancelled]);

  const reset = () => {
    setCustomer(""); setProduct(""); setDateFrom(""); setDateTo(""); setIncludeCancelled(false);
  };

  const exportCsv = async () => {
    try {
      const qs = new URLSearchParams(params).toString();
      // Use api.get for auth cookie propagation, then convert to blob download.
      const resp = await api.get(`/sales-history/export.csv${qs ? `?${qs}` : ""}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `sales-history-${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const fmt$ = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString(i18n.language === "ru" ? "ru-RU" : "en-US", {
        year: "numeric", month: "short", day: "numeric",
      })
      : "—";

  const statusPill = (s) => {
    const classes = {
      paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
      unpaid: "bg-amber-50 text-amber-700 border-amber-200",
      cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200 line-through",
    }[s] || "bg-zinc-100 text-zinc-700 border-zinc-200";
    return (
      <span
        className={`text-[10px] font-medium px-1.5 py-0.5 border rounded-full ${classes}`}
        data-testid={`sales-status-${s}`}
      >
        {t(`sales_history.status_${s}`)}
      </span>
    );
  };

  const editedTooltip = (r) => {
    const parts = [t("edit_log.edited_by", { who: r.edited_by_email || "", when: fmtDate(r.edited_at) })];
    if (r.original_values) {
      const ov = r.original_values;
      parts.push(
        `${t("edit_log.original_label")}: ${t("edit_log.original_qty")} ${ov.quantity ?? "—"}`
        + ` · ${t("edit_log.original_price")} ${ov.sale_price != null ? `$${Number(ov.sale_price).toFixed(2)}` : "—"}`
        + ` · ${t("edit_log.original_customer")} ${ov.to_user_name || ov.recipient_name || "—"}`
        + ` · ${t("edit_log.original_date")} ${ov.created_at ? fmtDate(ov.created_at) : "—"}`
        + (ov.note ? ` · ${t("edit_log.original_note")} "${ov.note}"` : "")
      );
    }
    return parts.join("\n");
  };

  const hasFilter = customer || product || dateFrom || dateTo || includeCancelled;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="sales-history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-800 mb-1"
            data-testid="sales-history-back"
          >
            <ArrowLeft size={12} weight="bold" /> {t("common.back")}
          </Link>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Receipt size={26} weight="bold" className="text-zinc-500" />
            {t("sales_history.title")}
          </h1>
          <p className="text-sm text-zinc-600 mt-0.5">{t("sales_history.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <BrutalButton
            size="md"
            variant="secondary"
            onClick={exportCsv}
            data-testid="sales-history-export"
            disabled={loading || items.length === 0}
          >
            <DownloadSimple size={14} weight="bold" /> {t("sales_history.export_csv")}
          </BrutalButton>
        </div>
      </div>

      {/* Sticky filter bar */}
      <div
        className="sticky top-14 z-20 -mx-4 sm:mx-0 bg-white/95 backdrop-blur border-y sm:border sm:rounded-xl border-zinc-200 p-3 sm:p-4"
        data-testid="sales-history-filter-bar"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
          <div className="relative">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <BrutalInput
              type="text"
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              placeholder={t("sales_history.filter_customer_placeholder")}
              className="pl-8"
              data-testid="filter-customer"
              aria-label={t("sales_history.filter_customer")}
            />
          </div>
          <div className="relative">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <BrutalInput
              type="text"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder={t("sales_history.filter_product_placeholder")}
              className="pl-8"
              data-testid="filter-product"
              aria-label={t("sales_history.filter_product")}
            />
          </div>
          <BrutalInput
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="filter-from"
            aria-label={t("sales_history.filter_from")}
            placeholder={t("sales_history.filter_from")}
          />
          <BrutalInput
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="filter-to"
            aria-label={t("sales_history.filter_to")}
            placeholder={t("sales_history.filter_to")}
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
          <label className="inline-flex items-center gap-2 text-xs font-medium text-zinc-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeCancelled}
              onChange={(e) => setIncludeCancelled(e.target.checked)}
              data-testid="filter-include-cancelled"
              className="w-4 h-4 accent-zinc-900"
            />
            {t("sales_history.include_cancelled")}
          </label>
          <div className="flex items-center gap-2">
            <div
              className="text-xs text-zinc-500 tabular-nums"
              data-testid="sales-history-summary"
            >
              {t("sales_history.summary", {
                count: totalCount,
                total: fmt$(totals.total_amount).replace("$", ""),
              })}
            </div>
            {hasFilter && (
              <button
                type="button"
                onClick={reset}
                className="text-xs font-medium text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1"
                data-testid="sales-history-reset"
              >
                <X size={12} weight="bold" /> {t("sales_history.reset")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      {loading && items.length === 0 ? (
        <div className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-100 rounded-md p-4">
          {t("sales_history.loading")}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center" data-testid="sales-history-empty">
          <p className="text-sm font-medium text-zinc-900">{t("sales_history.empty")}</p>
          <p className="text-xs text-zinc-500 mt-1">{t("sales_history.empty_hint")}</p>
        </div>
      ) : (
        <>
          {/* Desktop table (md+) */}
          <div className="hidden md:block bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2">{t("sales_history.col_date")}</th>
                  <th className="text-left px-3 py-2">{t("sales_history.col_customer")}</th>
                  <th className="text-left px-3 py-2">{t("sales_history.col_product")}</th>
                  <th className="text-right px-3 py-2">{t("sales_history.col_qty")}</th>
                  <th className="text-right px-3 py-2">{t("sales_history.col_unit_price")}</th>
                  <th className="text-right px-3 py-2">{t("sales_history.col_total")}</th>
                  <th className="text-left px-3 py-2">{t("sales_history.col_notes")}</th>
                  <th className="text-left px-3 py-2">{t("sales_history.col_status")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`sales-row-${r.id}`}
                    className={`hover:bg-zinc-50 ${r.status === "cancelled" ? "opacity-60" : ""}`}
                  >
                    <td className="px-3 py-2 text-zinc-600 whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-zinc-900 truncate max-w-[180px]" title={r.customer_name}>
                        {r.customer_name || "—"}
                      </div>
                      {r.customer_email && (
                        <div className="text-[11px] text-zinc-500 truncate max-w-[180px]">{r.customer_email}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-zinc-900 truncate max-w-[220px]" title={r.product_name}>
                      {r.product_name}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-700">{r.quantity}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-900 font-medium">
                      {fmt$(r.unit_price)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-900 font-semibold">
                      {fmt$(r.total)}
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-500 italic truncate max-w-[180px]" title={r.note || ""}>
                      {r.note || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        {statusPill(r.status)}
                        {r.edited && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800 text-[9px] font-bold tracking-wider uppercase"
                            title={editedTooltip(r)}
                            data-testid={`sales-edited-badge-${r.id}`}
                          >
                            <PencilSimple size={9} weight="bold" /> {t("edit_log.edited_badge")}
                          </span>
                        )}
                        {canEditLogs && r.status !== "cancelled" && (
                          <button
                            type="button"
                            onClick={() => setEditTarget(r)}
                            className="text-zinc-400 hover:text-zinc-800 p-0.5"
                            title={t("edit_log.button_tooltip")}
                            data-testid={`sales-edit-btn-${r.id}`}
                          >
                            <PencilSimple size={14} weight="bold" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card stack (<md) */}
          <div className="md:hidden space-y-2">
            {items.map((r) => (
              <div
                key={r.id}
                data-testid={`sales-card-${r.id}`}
                className={`bg-white border border-zinc-200 rounded-xl p-3 ${r.status === "cancelled" ? "opacity-60" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-900 truncate">{r.product_name}</div>
                    <div className="text-xs text-zinc-500 truncate mt-0.5">
                      {r.customer_name || "—"} · {fmtDate(r.date)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {r.edited && (
                      <span
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800 text-[9px] font-bold tracking-wider uppercase"
                        title={editedTooltip(r)}
                        data-testid={`sales-card-edited-badge-${r.id}`}
                      >
                        <PencilSimple size={9} weight="bold" /> {t("edit_log.edited_badge")}
                      </span>
                    )}
                    {statusPill(r.status)}
                    {canEditLogs && r.status !== "cancelled" && (
                      <button
                        type="button"
                        onClick={() => setEditTarget(r)}
                        className="text-zinc-400 hover:text-zinc-800 p-0.5"
                        title={t("edit_log.button_tooltip")}
                        data-testid={`sales-card-edit-btn-${r.id}`}
                      >
                        <PencilSimple size={14} weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex items-baseline justify-between">
                  <div className="text-xs text-zinc-500">
                    {r.quantity} × {fmt$(r.unit_price)}
                  </div>
                  <div className="text-base font-semibold tabular-nums text-zinc-900">{fmt$(r.total)}</div>
                </div>
                {r.note && (
                  <div className="mt-1.5 text-xs italic text-zinc-500 truncate" title={r.note}>
                    &ldquo;{r.note}&rdquo;
                  </div>
                )}
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-2">
              <BrutalButton
                variant="secondary"
                onClick={() => load(offset + 50, true)}
                disabled={loading}
                data-testid="sales-history-load-more"
              >
                {t("sales_history.load_more")}
              </BrutalButton>
            </div>
          )}
        </>
      )}

      {editTarget && (
        <EditMovementDialog
          row={{
            id: editTarget.id,
            quantity: editTarget.quantity,
            sale_price: editTarget.unit_price,
            customer_name: editTarget.customer_name || "",
            date: editTarget.date,
            note: editTarget.note || "",
            qtyEditable: true, // sales-history rows are always sale-type
          }}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(0, false); }}
        />
      )}
    </div>
  );
}
